/**
 * Single source of truth for per-step-type UI metadata (Phase I · Task 57).
 *
 * Every component in the `sequences` feature that needs a label, an icon,
 * the "manual task vs automatic" boolean, or the editor shape for a given
 * step type goes through `STEP_TYPE_META[step.type]`. This keeps the step
 * card, type picker, editor router, summary lines, and filter labels in
 * lockstep with the spec.
 *
 * Refs:
 * - docs/superpowers/specs/2026-05-07-outreach-04-sequences-design.md §9
 *   (step card visuals)
 * - docs/superpowers/specs/2026-05-07-outreach-04-sequences-design.md §10
 *   (per-type editor sheet shape)
 * - All `IconName`s verified against
 *   `frontend/node_modules/@central-icons-react/all/icons/index.d.ts`.
 */
import type { IconName } from "@/components/ui/icon";
import type { SequenceStepResponseType } from "@/lib/api/model/sequenceStepResponseType";

export type StepType = SequenceStepResponseType;

/**
 * Editor body family. Multiple types share the same body shape so the
 * per-type editor files can compose a common builder. e.g. `phone_call`,
 * `text_message`, `custom_task`, and `linkedin_profile_view` all render
 * a single Task Note editor + due/priority controls.
 */
export type StepEditorFamily =
  | "automatic_email"
  | "manual_email"
  | "task_note"
  | "linkedin_connection_request"
  | "linkedin_message";

export interface StepTypeMeta {
  type: StepType;
  /** Title rendered in the step card header and editor sheet header. */
  label: string;
  /** Shorter title used only in the editor sheet header when the full label is too wide. */
  editorLabel?: string;
  /** Centralicons name for the type icon (verified). */
  icon: IconName;
  /**
   * `true` when the step creates a manual `tasks` row that surfaces in the
   * Tasks tab and inbox. Drives whether the priority chip + due-date
   * controls show in the step card and editor.
   */
  is_manual_task: boolean;
  /** Discriminator for the editor router. */
  editor_family: StepEditorFamily;
}

export const STEP_TYPE_META: Record<StepType, StepTypeMeta> = {
  automatic_email: {
    type: "automatic_email",
    label: "Automatic Email",
    icon: "IconEmail1",
    is_manual_task: false,
    editor_family: "automatic_email",
  },
  manual_email: {
    type: "manual_email",
    label: "Manual Email",
    icon: "IconEmail1",
    is_manual_task: true,
    editor_family: "manual_email",
  },
  phone_call: {
    type: "phone_call",
    label: "Phone Call",
    icon: "IconCall",
    is_manual_task: true,
    editor_family: "task_note",
  },
  text_message: {
    type: "text_message",
    label: "Text Message",
    icon: "IconPhoneDynamicIsland",
    is_manual_task: true,
    editor_family: "task_note",
  },
  custom_task: {
    type: "custom_task",
    label: "Custom Task",
    icon: "IconTodos",
    is_manual_task: true,
    editor_family: "task_note",
  },
  linkedin_profile_view: {
    type: "linkedin_profile_view",
    label: "Social Profile View",
    icon: "IconPeopleCircle",
    is_manual_task: true,
    editor_family: "task_note",
  },
  linkedin_connection_request: {
    type: "linkedin_connection_request",
    label: "Social Connection Request",
    editorLabel: "Connection Request",
    icon: "IconPeopleCircle",
    is_manual_task: true,
    editor_family: "linkedin_connection_request",
  },
  linkedin_message: {
    type: "linkedin_message",
    label: "Social Message",
    icon: "IconPeopleCircle",
    is_manual_task: true,
    editor_family: "linkedin_message",
  },
};

/**
 * Canonical type ordering used in the type picker (spec §10 type
 * selector). Mirrors the Figma `28:66733` column order.
 */
export const STEP_TYPE_ORDER: StepType[] = [
  "automatic_email",
  "manual_email",
  "phone_call",
  "text_message",
  "custom_task",
  "linkedin_profile_view",
  "linkedin_connection_request",
  "linkedin_message",
];

/**
 * Format a human-friendly schedule clause for the step card summary line.
 *
 * Verbiage rules:
 *   - Automatic tasks (automatic_email):
 *       immediate → "Send immediately on enrollment"
 *       delay     → "Send after 2 business days @ 9:00 AM"
 *   - Manual tasks (everything else):
 *       immediate → "Schedule task immediately"
 *       delay     → "Schedule task immediately with due date in 2 business days"
 *       (manual tasks always schedule immediately; the delay sets the due date)
 */
export type ScheduleDelayUnit =
  | "weekdays"
  | "business_days"
  | "weeks"
  | "months";

/** Singular/plural human label for a delay unit. */
export function delayUnitLabel(
  type: ScheduleDelayUnit | null | undefined,
  n: number,
): string {
  switch (type) {
    case "weekdays":
      return n === 1 ? "day" : "days";
    case "weeks":
      return n === 1 ? "week" : "weeks";
    case "months":
      return n === 1 ? "month" : "months";
    case "business_days":
    default:
      return n === 1 ? "business day" : "business days";
  }
}

export function describeStepSchedule(args: {
  schedule_mode: "immediate" | "soonest_at" | "delay";
  schedule_delay_value?: number | null;
  schedule_delay_type?: ScheduleDelayUnit | null;
  schedule_at_time?: string | null;
  step_index: number;
  is_automatic: boolean;
}): string {
  const n = args.schedule_delay_value ?? 1;
  const unitLabel = delayUnitLabel(args.schedule_delay_type, n);
  const timeStr = args.schedule_at_time
    ? ` @ ${formatTimeOfDay(args.schedule_at_time)}`
    : "";

  if (args.is_automatic) {
    if (args.schedule_mode === "immediate") {
      return args.step_index === 0
        ? "Send immediately on enrollment"
        : "Send immediately after previous step";
    }
    if (args.schedule_mode === "soonest_at") {
      return `Send soonest at ${formatTimeOfDay(args.schedule_at_time ?? null)}`;
    }
    return `Send after ${n} ${unitLabel}${timeStr}`;
  }

  // Task steps — created immediately when the prior step completes; the
  // schedule is the DUE DATE. (soonest_at isn't offered for tasks.)
  if (args.schedule_mode === "delay") {
    return `Schedule task immediately with due date in ${n} ${unitLabel}${timeStr}`;
  }
  return "Schedule task immediately with no due date";
}

export function formatTimeOfDay(time: string | null): string {
  if (!time) return "9:00 AM";
  const [hh, mm] = time.split(":");
  const hour = Number.parseInt(hh ?? "0", 10);
  const minute = Number.parseInt(mm ?? "0", 10);
  if (Number.isNaN(hour) || Number.isNaN(minute)) return time;
  const am = hour < 12;
  const display = hour % 12 === 0 ? 12 : hour % 12;
  const padded = minute.toString().padStart(2, "0");
  return `${display}:${padded} ${am ? "AM" : "PM"}`;
}

const EMPTY_DOC = { type: "doc", content: [] };

/**
 * Card disclaimer explaining how a step is executed and what happens if the
 * user can't (or doesn't want to) action it. Automatic emails send on their
 * own, so they have none. Copy is kept smooth and em-dash-free.
 */
export function stepDisclaimer(type: StepType): string | null {
  switch (type) {
    case "manual_email":
      return (
        "Arbi creates this task right after the previous step (or on " +
        "enrollment if it's the first step). If your mailbox is connected, " +
        "send it from Arbi to track replies in the unified inbox. If you send " +
        "it outside Arbi we can't track the thread, so mark the task done or " +
        "skip it to resume the enrollment."
      );
    case "linkedin_message":
    case "linkedin_connection_request":
    case "linkedin_profile_view":
      return (
        "Arbi creates this task right after the previous step (or on " +
        "enrollment if it's the first step); a delay sets its due date. If " +
        "your Social account is connected and your plan supports it, it can " +
        "be executed automatically in Arbi. Otherwise complete it on " +
        "Social, then mark the task done or skip it to resume the enrollment."
      );
    case "phone_call":
      return (
        "Arbi creates this task right after the previous step (or on " +
        "enrollment if it's the first step). Arbi can't place calls, so make " +
        "the call outside Arbi, then mark the task done or skip it to resume " +
        "the enrollment."
      );
    case "text_message":
      return (
        "Arbi creates this task right after the previous step (or on " +
        "enrollment if it's the first step). Arbi can't send texts, so send " +
        "the message outside Arbi, then mark the task done or skip it to " +
        "resume the enrollment."
      );
    case "custom_task":
      return (
        "Arbi creates this task right after the previous step (or on " +
        "enrollment if it's the first step). Complete it, then mark it done " +
        "or skip it to resume the enrollment."
      );
    default:
      return null;
  }
}

/**
 * Build a minimal valid create payload for the given step type. Used by
 * the "Add a step" flow which creates the step in the backend first and
 * lets the user configure it afterwards.
 *
 * ``targetIndex`` is the step's final position in the sequence. A new
 * automatic email that isn't first defaults to a 2 business-day delay so
 * consecutive emails are spaced out automatically; everything else (and the
 * first step) defaults to immediate.
 */
export function buildDefaultStepPayload(
  type: StepType,
  targetIndex = 0,
): Record<string, unknown> {
  const schedule =
    type === "automatic_email" && targetIndex > 0
      ? {
          mode: "delay" as const,
          delay_value: 2,
          delay_type: "business_days" as const,
        }
      : { mode: "immediate" as const };
  const base = { type, schedule };

  switch (type) {
    case "automatic_email":
      return { ...base, subject_mode: "static", subject_doc: EMPTY_DOC, body_doc: EMPTY_DOC };
    case "manual_email":
      return { ...base, subject_mode: "static", subject_doc: EMPTY_DOC, body_doc: EMPTY_DOC };
    case "phone_call":
    case "text_message":
    case "custom_task":
      return { ...base, task_note_doc: EMPTY_DOC };
    case "linkedin_profile_view":
      return base;
    case "linkedin_connection_request":
      return { ...base, invitation_note: "" };
    case "linkedin_message":
      return { ...base, message_doc: EMPTY_DOC };
    default:
      return base;
  }
}

/**
 * Whether a step still needs configuration before the sequence can be
 * activated.
 *
 * This is computed entirely on the backend (see ``is_step_configured``
 * in ``backend/app/services/outreach/sequences.py``) and surfaced on
 * ``SequenceStepResponse.is_configured``. The frontend MUST NOT
 * recompute configuration from step content — it only reads the
 * backend-calculated boolean so the step card banner and the activation
 * gate can never drift from what the backend enforces.
 *
 * A missing flag is treated as configured (no banner) so older payloads
 * never produce a false "Setup Required" warning.
 */
export function isStepSetupRequired(step: {
  is_configured?: boolean | null;
}): boolean {
  return step.is_configured === false;
}

